import React, { useState } from 'react'
import { API_URL } from '../../lib/api-client'

const ERROR_IMG_SRC =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg=='

// CDN URL - You should set this to your CloudFront URL
const getCdnUrl = () => {
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_CDN_URL) {
    return import.meta.env.VITE_CDN_URL;
  }
  return '';
};

const CDN_URL = getCdnUrl();

const getFullImageUrl = (src: string | undefined | any): string => {
  // Return empty string for truly empty/missing images
  if (!src || typeof src !== 'string' || src.trim() === '') return '';
  
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:') || src.startsWith('blob:')) {
    // If CDN is configured and this is an S3 URL, replace with CDN
    if (CDN_URL && src.includes('.s3.') && src.includes('.amazonaws.com')) {
      // Extract the path after the bucket name
      const match = src.match(/\.amazonaws\.com\/(.+)$/);
      if (match) {
        const path = match[1];
        return `${CDN_URL}/${path}`;
      }
    }
    return src;
  }
  
  const backendUrl = CDN_URL || API_URL;
  
  if (src.startsWith('/')) {
    return `${backendUrl}${src}`;
  }
  
  return `${backendUrl}/${src}`;
};

export function ImageWithFallback(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [didError, setDidError] = useState(false)

  const handleError = () => {
    setDidError(true)
  }

  const { src, alt, style, className, loading = 'lazy', ...rest } = props
  const fullSrc = getFullImageUrl(src)

  // Helper to get initials from alt text
  const getInitialsFromAlt = (altText?: string): string | null => {
    if (!altText) return null;
    
    // Remove common prefixes
    const cleanText = altText.replace(/^(profile of|photo of|image of)\s+/i, '').trim();
    
    // Split by space and get first letters
    const parts = cleanText.split(' ').filter(p => p.length > 0);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    } else if (parts.length === 1 && parts[0].length >= 2) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return null;
  };

  // Helper to get background color based on text
  const getBgColor = (text: string): string => {
    const colors = [
      'bg-rose-500',
      'bg-pink-500',
      'bg-purple-500',
      'bg-indigo-500',
      'bg-blue-500',
      'bg-cyan-500',
      'bg-teal-500',
      'bg-green-500',
      'bg-amber-500',
      'bg-orange-500'
    ];
    
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // If no src provided or empty, show placeholder immediately
  if (!fullSrc || fullSrc === '') {
    const initials = getInitialsFromAlt(alt);
    
    if (initials) {
      // Show initials if we can extract them from alt
      return (
        <div
          className={`inline-flex items-center justify-center text-white font-semibold ${getBgColor(alt || '')} ${className ?? ''}`}
          style={style}
          {...rest}
        >
          {initials}
        </div>
      );
    }
    
    return (
      <div
        className={`inline-block bg-gradient-to-br from-pink-100 to-purple-100 text-center align-middle ${className ?? ''}`}
        style={style}
      >
        <div className="flex items-center justify-center w-full h-full">
          <img src={ERROR_IMG_SRC} alt="No image" {...rest} />
        </div>
      </div>
    )
  }

  // If image failed to load, show initials or error icon
  if (didError) {
    const initials = getInitialsFromAlt(alt);
    
    if (initials) {
      // Show initials if we can extract them from alt
      return (
        <div
          className={`inline-flex items-center justify-center text-white font-semibold ${getBgColor(alt || '')} ${className ?? ''}`}
          style={style}
          {...rest}
        >
          {initials}
        </div>
      );
    }
    
    return (
      <div
        className={`inline-block bg-gray-100 text-center align-middle ${className ?? ''}`}
        style={style}
      >
        <div className="flex items-center justify-center w-full h-full">
          <img src={ERROR_IMG_SRC} alt="Error loading image" {...rest} data-original-url={src} />
        </div>
      </div>
    );
  }

  return (
    <img 
      src={fullSrc} 
      alt={alt} 
      className={className} 
      style={style} 
      loading={loading}
      decoding="async"
      {...rest} 
      onError={handleError} 
    />
  )
}